import { useState } from 'react';
import { Avatar } from '../components/Avatar';
import { Button } from '../components/Button';
import { SegmentedControl } from '../components/SegmentedControl';
import { Switch } from '../components/Switch';
import { Vine } from '../components/Vine';
import { PLANT_DETAILS } from '../state/plantGrowth';
import { allModes } from '../engine/registry';
import { useNav, type Screen } from '../state/NavContext';
import { useSiteConfig } from '../state/SiteConfigContext';
import { AVATARS, useProfile, type AvatarId } from '../state/ProfileContext';
import { useSettings, type ThemePref } from '../state/SettingsContext';
import { countWord } from '../utils/format';

const STEPS = 4;

export function OnboardingScreen() {
  const { create } = useProfile();
  const { settings, update, effectiveReducedMotion } = useSettings();
  const { navigate } = useNav();
  const { isModeEnabled } = useSiteConfig();
  const modes = allModes().filter((m) => isModeEnabled(m.id));
  const MODE_COUNT = modes.length;
  const starters = modes.slice(0, 3).map((m) => m.name);
  const [step, setStep] = useState(0);
  const [name, setName] = useState('');
  const [avatar, setAvatar] = useState<AvatarId>('sprout');

  const next = () => setStep((s) => Math.min(STEPS - 1, s + 1));
  // Create the profile, then land on the chosen screen. NavProvider sits above
  // the onboarding gate, so the route set here survives into the main shell.
  const finishAt = (screen: Screen) => {
    navigate(screen);
    create(name, avatar);
  };

  return (
    <div className="cv-onboarding">
      {/* Clean sprout mark (matches the favicon / top bar), shown on every step. */}
      <header className="cv-onboarding__brand">
        <svg className="cv-onboarding__mark" viewBox="0 0 32 32" fill="none" aria-hidden="true">
          <path d="M16 28V12" stroke="var(--vine)" strokeWidth="2.4" strokeLinecap="round" />
          <path d="M15 18C9 17 6 13 6 8c5 0 9 3 9 10Z" fill="var(--vine)" />
          <path d="M17 14c6-1 9-5 9-10-5 0-9 4-9 10Z" fill="var(--vine-bright)" />
          <circle cx="16" cy="29" r="2" fill="var(--seed)" />
        </svg>
        <span className="cv-onboarding__wordmark">
          Clue<em>Vine</em>
        </span>
      </header>

      <div className="cv-onboarding__dots" aria-hidden="true">
        {Array.from({ length: STEPS }, (_, i) => (
          <span key={i} className="cv-onboarding__dot" data-active={i === step ? 'true' : undefined} />
        ))}
      </div>

      {step === 0 && (
        <div className="cv-onboarding__step">
          <p className="cv-home__tagline">
            A cozy daily word garden. Solve a puzzle a day across {countWord(MODE_COUNT)} modes and watch
            your streak grow.
          </p>
          <Button variant="primary" size="lg" block onClick={next}>
            Get started
          </Button>
        </div>
      )}

      {step === 1 && (
        <div className="cv-onboarding__step">
          <h2>What should we call you?</h2>
          <div className="cv-field" style={{ width: '100%' }}>
            <input
              className="cv-input"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Your name"
              maxLength={24}
              aria-label="Display name"
            />
          </div>
          <span className="cv-label" style={{ alignSelf: 'flex-start' }}>
            Choose your growing plant
          </span>
          <div className="cv-avatar-grid">
            {AVATARS.map((a) => (
              <button
                key={a}
                type="button"
                className="cv-avatar-pick"
                aria-pressed={a === avatar}
                aria-label={`${a} avatar`}
                onClick={() => setAvatar(a)}
              >
                <Avatar id={a} size={44} />
              </button>
            ))}
          </div>
          <div className="cv-plant-preview" aria-live="polite">
            <Vine avatar={avatar} streak={7} size={104} reducedMotion={effectiveReducedMotion} />
            <p className="cv-help">{PLANT_DETAILS[avatar].description} This is your plant at a 7-day streak.</p>
          </div>
          <Button variant="primary" block onClick={next}>
            Continue
          </Button>
        </div>
      )}

      {step === 2 && (
        <div className="cv-onboarding__step">
          <h2>Choose your look</h2>
          <p className="cv-muted">You can change everything later in Settings.</p>
          <SegmentedControl<ThemePref>
            block
            ariaLabel="Theme"
            value={settings.theme}
            onChange={(t) => update({ theme: t })}
            options={[
              { value: 'light', label: 'Light' },
              { value: 'system', label: 'Auto' },
              { value: 'dark', label: 'Dark' },
            ]}
          />
          <div className="cv-setting-row" style={{ width: '100%' }}>
            <div className="cv-setting-row__text">
              <span className="cv-setting-row__title">Color-blind-friendly clues</span>
              <span className="cv-setting-row__desc">Blue / orange clues with patterns</span>
            </div>
            <Switch
              label="Color-blind-friendly clues"
              checked={settings.colorBlind}
              onChange={(v) => update({ colorBlind: v })}
            />
          </div>
          <Button variant="primary" block onClick={next}>
            Continue
          </Button>
        </div>
      )}

      {step === 3 && (
        <div className="cv-onboarding__step">
          <h2>You’re all set{name ? `, ${name}` : ''}!</h2>
          <p className="cv-muted">
            {starters.length > 0
              ? `Start with today’s ${starters[0]}${starters.length > 1 ? `, or wander over to ${starters.slice(1).join(' and ')}` : ''}. `
              : ''}
            Your streak begins now.
          </p>
          <Button variant="primary" size="lg" block onClick={() => finishAt('home')}>
            Enter the garden
          </Button>
          <Button variant="ghost" block iconLeft="help" onClick={() => finishAt('howto')}>
            See all {countWord(MODE_COUNT)} modes first
          </Button>
        </div>
      )}
    </div>
  );
}
